/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { MonolithBuyPanel } from '@components/systems/monolith/MonolithBuyPanel'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { Analytics, buildProductJsonLd, buildBreadcrumbJsonLd } from '@utils'
import { SEO_SITE, getSeoEntry } from '@src/configs/seo.config'
import { getShopDetail } from '@src/configs/shopDetail.config'

import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'

interface PropsType {
	/** 대문자 제품 코드 — KDM6 · KDM4 · AMP2 · DS. 라우트가 넣는다 */
	code: string
}

/**
 * 구매 화면 `/shop/{code}` (2026-09-10 얇게 정리, SITEMAP_PLAN §17).
 *
 * **이걸 산다.** 한 밴드로 끝난다 — 좌 갤러리 / 우 [제목 · 가격 · 옵션 · 수량 · Add to cart ·
 * 확인용 서너 줄 · 참조 링크]. 사양 전체표·호환 장비·본문 이미지·설치 절차는 `/products/{code}` 로
 * 갔고, 여기서는 "Specification" 참조 행 하나로 보낸다.
 *
 * `noindex` 다 — 색인 정본은 `/products/{code}` (§16.1).
 */
const MonolithShopDetailPage = (props: PropsType) => {
	// hook
	const navigate = useNavigate()

	const detail = getShopDetail(props.code)

	// redux state
	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	const thisProduct: SeasonProductType = React.useMemo(() => {
		const found = seasonProductList.filter(
			aProduct => (aProduct.productCode ?? '').toUpperCase() === props.code.toUpperCase(),
		)
		return found.length === 0 ? new SeasonProduct() : found[0]
	}, [seasonProductList, props.code])

	const [galleryIdx, setGalleryIdx] = React.useState<number>(0)

	/*
		참조 행 Shipping 의 값 — 사실 한 줄 (PRICING §8.4). 관세는 어느 목적지든 우리가 내므로 "included".
		배송비는 서버 규칙(소계 $100 미만 $20)이라 값이 $100 아래인 액세서리만 결제로 보낸다.
		VAT·GST 가 배송 시 붙는다는 사실은 구매 패널 CTA 아래 키커가 말한다. 사용자가 이 문구를
		남기기로 했다(2026-09-17) — 위 키커와 겹치지만 정책 링크의 라벨 역할이다.
	*/
	const shippingLine: string =
		thisProduct.seasonUsd > 0 && thisProduct.seasonUsd < 100
			? 'Import duties included. Shipping is shown at checkout'
			: 'Shipping and import duties included'

	// 제품이 바뀌면(라우트 이동) 갤러리를 첫 장으로 되돌린다.
	React.useEffect(() => setGalleryIdx(0), [props.code])

	// 구매 화면을 봤다 — 구매 퍼널의 첫 단계 (utils/Analytics)
	React.useEffect(() => {
		if (detail !== undefined) {
			Analytics.viewItem({ code: detail.code, name: detail.name })
		}
	}, [props.code])

	const path = `/shop/${props.code.toLowerCase()}`
	const productPath = `/products/${props.code.toLowerCase()}`

	const jsonLd = React.useMemo(() => {
		if (detail === undefined) return []

		return [
			buildProductJsonLd({
				name: detail.name,
				description: getSeoEntry(path).description,
				path: path,
				image: `${SEO_SITE.origin}${detail.gallery[0].src}`,
				product: thisProduct,
				sku: detail.code,
			}),
			buildBreadcrumbJsonLd([
				{ name: 'Home', path: '/' },
				// 구매 화면이라 부모가 Shop 이다. 제품 설명은 /products/{code}.
				{ name: 'Shop', path: '/shop' },
				{ name: detail.name, path: path },
			]),
		]
	}, [detail, thisProduct, path])

	if (detail === undefined) {
		return null
	}

	const go = (to: string) => (e: React.MouseEvent) => {
		e.preventDefault()
		navigate(to)
	}

	/** 제목 — 28px 이상이므로 규정이 있는 제품은 워드마크로만 (PRODUCT_NAMES §0) */
	const renderTitle = () => {
		if (detail.wordmark === 'koinos') {
			return <KoinosWordmark as="span" />
		}
		if (detail.wordmark === 'kedem') {
			return <KedemWordmark as="span" model={detail.model} />
		}
		return detail.name
	}

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="mnWrap">
				<View className="mnDetailTop">
					<a className="mnDetailBack" href="/shop" onClick={go('/shop')}>
						Shop
					</a>
				</View>

				{/* 이 페이지의 전부 — 좌 갤러리 / 우 구매. 페이지의 유일한 블랙 CTA 가 여기 있다 */}
				<View className="mnDetailBuy">
					<View className="mnDetailMedia">
						<View className="mnDetailShot">
							<img
								src={detail.gallery[galleryIdx].src}
								alt={detail.gallery[galleryIdx].alt}
								className="mnDetailShotImage"
							/>
						</View>

						{detail.gallery.length > 1 && (
							<View className="mnDetailThumbs">
								{detail.gallery.map((aShot, idx) => (
									<button
										key={aShot.src}
										type="button"
										className={`mnDetailThumb ${idx === galleryIdx ? 'on' : ''}`}
										onClick={() => setGalleryIdx(idx)}
										aria-label={`${detail.name}, view ${idx + 1}`}
										aria-current={idx === galleryIdx}
									>
										<img src={aShot.src} alt="" className="mnDetailThumbImage" />
									</button>
								))}
							</View>
						)}
					</View>

					<View className="mnDetailText">
						<h1 className="mnDetailTitle">{renderTitle()}</h1>
						<p className="mnDetailTagline">{detail.tagline}</p>

						<MonolithBuyPanel product={thisProduct} />

						{/* 살 때 확인하는 것만. "내가 맞는 걸 사는가"에 답하고 끝낸다 */}
						<View className="mnBuyFacts">
							{detail.summary.map(aRow => (
								<View key={aRow.label} className="mnBuyFact">
									<span className="mnBuyFactLabel">{aRow.label}</span>
									<span className="mnBuyFactValue">{aRow.value}</span>
								</View>
							))}
						</View>

						{/* Specification 은 페이지 하단 클로즈로 올라갔다 — 같은 링크를 두 번 두지 않는다 */}
						<View className="mnDetailRefs">
							{detail.manualCode !== undefined && (
								<a className="mnDetailRef" href="/manuals" onClick={go('/manuals')}>
									<span className="mnDetailRefLabel">Manual</span>
									<span className="mnDetailRefValue">PDF, five languages</span>
								</a>
							)}
							<a
								className="mnDetailRef"
								href="/store-policy"
								onClick={go('/store-policy')}
							>
								<span className="mnDetailRefLabel">Shipping</span>
								<span className="mnDetailRefValue">{shippingLine}</span>
							</a>
						</View>
					</View>
				</View>

				{/*
					제품 페이지로 보내는 클로즈. 묻지 않고 **저쪽에 무엇이 있는지 말한다**
					(PERSONA §1 규칙 1 — 설득·권유 문형 금지). 버튼은 고스트다: 이 페이지의
					블랙 CTA 는 Add to cart 하나여야 한다 (DESIGN_SYSTEM §5).
				*/}
				<View className="mnBand mnShopMore">
					<p className="mnShopMoreLine">{detail.moreLine}</p>
					<a
						className="mnCtaGhost mnShopMoreCta"
						href={productPath}
						onClick={go(productPath)}
					>
						{`${detail.name} product page`}
					</a>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithShopDetailPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	'.mnDetailTop': {
		paddingTop: 'clamp(28px, 3.2vw, 46px)',

		/** 상위로 올라가는 링크. 셰브론은 Support 인덱스 전용이라 여기서는 화살표 글리프 하나 */
		'.mnDetailBack': {
			display: 'inline-block',
			fontSize: 13,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			transition: 'color 200ms ease-out',

			'&::before': {
				content: '"\\2190"',
				marginRight: 12,
			},
			'&:hover': { color: mono.ink },
		},
	},

	/**
	 * 구매 밴드 — 미디어 7 : 패널 5. 구매 화면에서 가장 큰 것은 제품이다.
	 * 2026-09-10: 아래에 있던 밴드들이 `/products/{code}` 로 가서 이 밴드가 페이지의 전부가 됐다.
	 */
	'.mnDetailBuy': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
		gap: 'clamp(32px, 4.4vw, 72px)',
		padding: 'clamp(36px, 4vw, 56px) 0 clamp(56px, 6.6vw, 90px)',
		alignItems: 'start',

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 40,
		},
	},

	/**
	 * 사진은 보더 안에 둔다. 2026-09-15 에 열대 배경 합성 컷(SITEMAP_PLAN §13.4)은 걷었지만, 갤러리에는
	 * 아직 배경 있는 촬영 컷(검은 판·흡음재·무지 배경)이 섞여 있어 한 틀로 묶는 쪽이 조용하다.
	 */
	'.mnDetailShot': {
		width: '100%',
		border: `1px solid ${mono.line}`,
		background: mono.surface,

		'.mnDetailShotImage': {
			display: 'block',
			width: '100%',
			aspectRatio: '1.4 / 1',
			objectFit: 'cover',
		},
	},

	'.mnDetailThumbs': {
		display: 'grid',
		gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
		gap: 12,
		marginTop: 12,

		'.mnDetailThumb': {
			padding: 0,
			border: `1px solid ${mono.line}`,
			background: mono.surface,
			cursor: 'pointer',
			transition: 'border-color 200ms ease-out',

			'.mnDetailThumbImage': {
				display: 'block',
				width: '100%',
				aspectRatio: '1.4 / 1',
				objectFit: 'cover',
			},

			'&:hover': { borderColor: mono.lineSoft },
			'&.on': { borderColor: mono.ink },
		},

		'@media (max-width: 600px)': {
			gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		},
	},

	'.mnDetailText': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',

		'.mnDetailTitle': {
			fontSize: 'clamp(34px, 3.4vw, 48px)',
			lineHeight: 1,
			fontWeight: 300,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},

		'.mnDetailTagline': {
			marginTop: 18,
			fontSize: 'clamp(18px, 1.6vw, 22px)',
			lineHeight: 1.35,
			fontWeight: 200,
			color: mono.dim,
		},

		/** 구매 패널 — 제목에서 한 칸 더 떨어뜨린다 */
		'> div:not(.mnBuyFacts):not(.mnDetailRefs)': {
			marginTop: 'clamp(32px, 3.4vw, 48px)',
			width: '100%',
		},
	},

	/**
	 * 확인용 서너 줄. 헤어라인 행이되 링크가 아니다 — 참조 행(`.mnDetailRefs`)과 같은 리듬으로
	 * 이어 붙어 패널 하단이 한 묶음으로 읽힌다.
	 */
	'.mnBuyFacts': {
		width: '100%',
		marginTop: 'clamp(36px, 3.6vw, 52px)',
		borderTop: `1px solid ${mono.line}`,

		'.mnBuyFact': {
			display: 'flex',
			alignItems: 'baseline',
			justifyContent: 'space-between',
			gap: 16,
			padding: '16px 0',
			borderBottom: `1px solid ${mono.line}`,

			'.mnBuyFactLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
				whiteSpace: 'nowrap',
			},
			'.mnBuyFactValue': {
				fontSize: 15,
				lineHeight: 1.5,
				fontWeight: 300,
				color: mono.ink,
				textAlign: 'right',
			},
		},
	},

	/** 나가는 링크 — 사양 전체는 제품 페이지, 매뉴얼과 정책은 각자의 자리로 */
	'.mnDetailRefs': {
		width: '100%',

		'.mnDetailRef': {
			display: 'flex',
			alignItems: 'baseline',
			justifyContent: 'space-between',
			gap: 16,
			padding: '16px 0',
			borderBottom: `1px solid ${mono.line}`,
			transition: 'padding-left 200ms ease-out',

			'.mnDetailRefLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
				whiteSpace: 'nowrap',
			},
			'.mnDetailRefValue': {
				fontSize: 14,
				fontWeight: 400,
				color: mono.dim,
				textAlign: 'right',
			},

			'&:hover': {
				paddingLeft: 8,
				'.mnDetailRefValue': { color: mono.ink },
			},
		},
	},

	/**
	 * 클로즈 — 중앙 정렬. DESIGN_SYSTEM §3 은 히어로·클로즈·시즌 밴드만 중앙을 허용한다.
	 * 제품 페이지가 블랙 "Buy" 로 닫히고, 구매 화면은 고스트 "product page" 로 닫힌다.
	 */
	'.mnShopMore': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(64px, 8vw, 110px) 0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',

		'.mnShopMoreLine': {
			fontSize: 'clamp(22px, 2.1vw, 30px)',
			lineHeight: 1.4,
			fontWeight: 200,
			color: mono.ink,
			maxWidth: 720,
		},

		'.mnShopMoreCta': {
			marginTop: 'clamp(28px, 3vw, 40px)',
			textDecoration: 'none',
			transition: 'background-color 200ms ease-out, color 200ms ease-out',

			'&:hover': {
				background: mono.ink,
				color: '#FFFFFF',
			},
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnDetailBack, .mnDetailThumb, .mnDetailRef, .mnShopMoreCta': {
			transition: 'none',
		},
	},
})
